/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */

import React from "react";
import { Area, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { ChartConfig, MetricKey, TimeSeriesDataPoint } from "./MetricsBarChartView";

interface MetricsLineChartViewProps<T extends TimeSeriesDataPoint> {
  data: T[];
  config: ChartConfig<T>;

  // Visualization variant
  chartType?: "area" | "line";

  // Chart dimensions & styling
  height?: string | number;
  showLegend?: boolean;

  // X-axis configuration
  rotateXAxisLabels?: boolean;
  xAxisInterval?: number | "preserveStart" | "preserveEnd" | "preserveStartEnd";
  xAxisTicks?: string[];
  xAxisPadding?: { left?: number; right?: number };

  // Dual Y-axes (first 2 metrics only)
  yAxisConfig?: {
    left?: { width?: number };
    right?: { width?: number };
  };

  // Chart margins
  margin?: {
    top?: number;
    right?: number;
    left?: number;
    bottom?: number;
  };
}

export const MetricsLineChartView = <T extends TimeSeriesDataPoint>({
  data,
  config,
  chartType = "area",
  height,
  showLegend = true,
  rotateXAxisLabels,
  xAxisInterval,
  xAxisTicks,
  xAxisPadding,
  yAxisConfig,
  margin,
}: MetricsLineChartViewProps<T>) => {
  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  // Early return if no data
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Early return if no metrics configured
  if (!config.metrics || config.metrics.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No metrics selected</p>
      </div>
    );
  }

  // Get formatters with defaults
  const getFormatter = (metric: MetricKey<T>) => {
    return config.formatters[metric] || ((v: number) => v.toString());
  };

  const getColor = (metric: MetricKey<T>) => {
    return config.colors[metric] || "#6b7280";
  };

  const getLabel = (metric: MetricKey<T>) => {
    return config.labels[metric] || (metric as string);
  };

  // Custom tooltip with viewport boundary detection
  const CustomTooltip = ({ active, payload, label, coordinate, viewBox }: any) => {
    if (!active || !payload || !payload.length) return null;

    const tooltipWidth = 250;
    const viewportWidth = window.innerWidth;

    // Get the chart's position on screen using the ref
    let absoluteX = coordinate?.x || 0;
    if (chartContainerRef.current) {
      const chartRect = chartContainerRef.current.getBoundingClientRect();
      absoluteX = chartRect.left + (coordinate?.x || 0);
    }

    const wouldOverflowRight = absoluteX + tooltipWidth + 10 > viewportWidth - 20;

    // Calculate approximate data point width for line charts
    const chartWidth = viewBox?.width || 0;
    const dataLength = data?.length || 1;
    const widthPerPoint = chartWidth / dataLength;
    const offsetFromCenter = widthPerPoint / 2 + 10;

    return (
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          padding: "8px 12px",
          zIndex: 1000,
          transform: wouldOverflowRight ? `translateX(calc(-100% - ${offsetFromCenter}px))` : undefined,
          pointerEvents: "none",
        }}
      >
        <p style={{ margin: "0 0 4px 0", fontWeight: 600 }}>{label}</p>
        {payload.map((entry: any) => {
          const metric = entry.dataKey as MetricKey<T>;
          const formatter = getFormatter(metric);
          const metricLabel = getLabel(metric);
          return (
            <div key={entry.dataKey} style={{ display: "flex", alignItems: "center", gap: "8px", margin: "4px 0" }}>
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: entry.stroke }}></div>
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                {metricLabel}: <strong>{formatter(entry.value)}</strong>
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Determine if we should use dual Y-axes (only for exactly 2 metrics)
  const useDualYAxes = config.metrics.length === 2;

  // Dynamic X-axis configuration based on data density
  const shouldRotateLabels = rotateXAxisLabels !== undefined ? rotateXAxisLabels : data.length > 10;
  const xAxisConfig = shouldRotateLabels
    ? { angle: -45, textAnchor: "end" as const, height: 60 }
    : { angle: 0, textAnchor: "middle" as const, height: 30 };
  const defaultBottomMargin = shouldRotateLabels ? 60 : 20;

  // Merge default margins with custom margins
  const chartMargin = {
    top: margin?.top ?? 20,
    right: margin?.right ?? 30,
    left: margin?.left ?? 30,
    bottom: margin?.bottom ?? defaultBottomMargin,
  };

  return (
    <div className="h-full flex flex-col min-h-0" style={{ transition: "none", height }}>
      {/* Metrics Legend */}
      {showLegend && (
        <div className="flex items-center gap-6 mb-4 flex-wrap">
          {config.metrics.map((metric) => (
            <div key={metric as string} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getColor(metric) }}></div>
              <span className="text-sm font-medium text-gray-900">{getLabel(metric)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Chart */}
      <div className="flex-1 min-h-0 transition-none" ref={chartContainerRef}>
        <ResponsiveContainer width="100%" height="100%" debounce={0}>
          <ComposedChart data={data} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
              angle={xAxisConfig.angle}
              textAnchor={xAxisConfig.textAnchor}
              height={xAxisConfig.height}
              interval={xAxisTicks ? 0 : xAxisInterval}
              ticks={xAxisTicks}
              padding={xAxisPadding || { left: 0, right: 0 }}
            />

            {useDualYAxes ? (
              // Dual Y-axes for exactly 2 metrics (old behavior)
              <>
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={false}
                  width={yAxisConfig?.left?.width || 70}
                  tickFormatter={(value) => getFormatter(config.metrics[0])(value)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={false}
                  width={yAxisConfig?.right?.width || 70}
                  tickFormatter={(value) => getFormatter(config.metrics[1])(value)}
                />
              </>
            ) : (
              // Single Y-axis for more than 2 metrics (new behavior)
              <YAxis
                tick={{ fontSize: 12, fill: "#6b7280" }}
                tickLine={false}
                axisLine={false}
                width={yAxisConfig?.left?.width || 70}
                tickFormatter={(value) => getFormatter(config.metrics[0])(value)}
              />
            )}

            <Tooltip
              wrapperStyle={{
                zIndex: 1000,
              }}
              content={<CustomTooltip />}
              allowEscapeViewBox={{ x: true, y: true }}
            />
            {config.metrics.map((metric, index) => (
              <Area
                key={metric as string}
                type="monotone"
                dataKey={metric as string}
                fill={chartType === "area" ? getColor(metric) : "transparent"}
                fillOpacity={chartType === "area" ? 0.1 : 0}
                stroke={getColor(metric)}
                strokeWidth={2}
                isAnimationActive={false}
                yAxisId={useDualYAxes ? (index === 0 ? "left" : "right") : undefined}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
